/**
 * Serialize data to application/x-www-form-urlencoded format
 *
 * Converts an object to a URL-encoded string suitable for form POST requests.
 * Undefined and null values are omitted from the output.
 * All values are converted to strings and properly URL-encoded.
 *
 * @param data - Object to serialize
 * @returns URL-encoded string
 *
 * @example
 * ```ts
 * serializeFormData({ name: 'John Doe', age: 30, active: true })
 * // Returns: "name=John+Doe&age=30&active=true"
 *
 * serializeFormData({ fontip: 'YAT', bastarih: '01.12.2024' })
 * // Returns: "fontip=YAT&bastarih=01.12.2024"
 * ```
 *
 * @since 1.0.0
 */
export function serializeFormData(
  data: Record<string, any>
): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  }

  return params.toString();
}

/**
 * Build query string from params object
 *
 * Converts an object to a query string with a leading '?'.
 * Undefined and null values are omitted from the output.
 * Returns an empty string if no valid parameters are provided.
 *
 * @param params - Object with query parameters
 * @returns Query string with leading '?' or empty string
 *
 * @example
 * ```ts
 * buildQueryString({ page: 1, limit: 10, active: true })
 * // Returns: "?page=1&limit=10&active=true"
 *
 * buildQueryString({})
 * // Returns: ""
 *
 * buildQueryString({ search: 'test query', page: 1 })
 * // Returns: "?search=test+query&page=1"
 * ```
 *
 * @since 1.0.0
 */
export function buildQueryString(
  params: Record<string, string | number | boolean>
): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

